import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ComponentDimensions } from '../../../../types/Layout';
import { colors } from '../../../../colors';
import { Subscription } from '../../../../types/subscription';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
} from 'victory';
import useAnimatedChartData from '../../../../hooks/useAnimatedChartData';
import { OverviewChartTimeframe, OverviewChartType } from '../../types';
import { useSelector } from 'react-redux';
import { ChartType } from '../../../Detail/Charts/ProfitChart';
import LoadingIcons from 'react-loading-icons';
import {
  calculateLongestTickWidth,
  calculateYAxisWidth,
  calculateYTicks,
  stretchYValueOnSharedDomain,
} from '../../../../helpers/Chart';
import moment from 'moment';
import {
  createXTickValues,
  getFormatUnit,
  isCustomTransaction,
} from '../../../../helpers/bundleTransactionsToCompactForm';
import { formatPrice } from '../../../../helpers/NumberFormatter';
import {
  selectOverviewChartTimeframe,
  selectOverviewChartType,
} from '../../../../store/slices/ui';
import { SummaryInformation as SummaryInformationType } from '../../types';
import { DataPoint } from '../../../Detail/Charts/types';

const SummaryChart = ({
  subscriptions,
  setSummaryInformation,
  totalValue,
}: {
  subscriptions: Subscription[];
  setSummaryInformation: React.Dispatch<
    React.SetStateAction<SummaryInformationType | undefined>
  >;
  totalValue: number;
}) => {
  const overviewChartTimeframe = useSelector(selectOverviewChartTimeframe);
  const overviewChartType = useSelector(selectOverviewChartType);

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);
  const rootRef = useRef<any>(null);

  useEffect(() => {
    if (!rootRef.current) {
      return;
    }

    if (!!rootDimensions) {
      return;
    }

    const parentRect = rootRef.current.getBoundingClientRect();
    setRootDimensions({
      width: parentRect.width,
      height: parentRect.height,
    });
  }, [rootDimensions]);

  const allPossibleNapTimestamps = useMemo(() => {
    return subscriptions
      .reduce<number[]>((acc, curr) => {
        const timestamps =
          curr.chartData?.dataPoints.noAlgorithmicProgression.map((dp) => {
            let timestamp = dp.timestamp;
            if (timestamp.toString().slice(-1) === '9') {
              timestamp += 1;
            }
            return timestamp;
          }) ?? [];
        acc.push(...timestamps);
        return acc;
      }, [])
      .filter((v, i, s) => s.findIndex((si) => si === v) === i)
      .sort();
  }, [subscriptions]);

  const allPossibleNapTimestampsByTimeframe = useMemo(() => {
    return allPossibleNapTimestamps.filter((timestamp) => {
      switch (overviewChartTimeframe) {
        case OverviewChartTimeframe.All:
          return true;

        case OverviewChartTimeframe.TwoYears:
          return moment().subtract(2, 'years').valueOf() <= timestamp;

        case OverviewChartTimeframe.OneYear:
          return moment().subtract(1, 'year').valueOf() <= timestamp;

        case OverviewChartTimeframe.SixMonths:
          return moment().subtract(6, 'months').valueOf() <= timestamp;

        case OverviewChartTimeframe.ThreeMonths:
          return moment().subtract(3, 'months').valueOf() <= timestamp;

        case OverviewChartTimeframe.TwoMonths:
          return moment().subtract(2, 'months').valueOf() <= timestamp;

        case OverviewChartTimeframe.OneMonth:
          return moment().subtract(1, 'month').valueOf() <= timestamp;

        case OverviewChartTimeframe.OneWeek:
          return moment().subtract(1, 'week').valueOf() <= timestamp;

        default:
          break;
      }
    });
  }, [allPossibleNapTimestamps, overviewChartTimeframe]);

  const {
    rawNapData,
    rawTotalStableData,
    nettoNapData,
    nettoTotalStableData,
    rawCostBasisData,
  } = useMemo(
    () =>
      subscriptions.reduce<{
        rawNapData: Record<
          number,
          { x: number; y: number; hasIndicator: boolean | undefined }
        >;
        rawTotalStableData: Record<
          number,
          { x: number; y: number; hasIndicator: boolean }
        >;
        nettoNapData: Record<
          number,
          { x: number; y: number; hasIndicator: boolean | undefined }
        >;
        nettoTotalStableData: Record<
          number,
          { x: number; y: number; hasIndicator: boolean }
        >;
        rawCostBasisData: Record<number, { x: number; y: number }>;
      }>(
        (acc, curr) => {
          const refactorDatapoints = (
            source: DataPoint[] | undefined,
            target: Record<
              number,
              {
                x: number;
                y: number;
                hasIndicator: boolean | undefined;
              }
            >,
            firstValue: number,
          ) => {
            source?.forEach((datapoint) => {
              let timestamp = datapoint.timestamp;
              if (timestamp.toString().slice(-1) === '9') {
                timestamp += 1;
              }

              if (!allPossibleNapTimestampsByTimeframe.includes(timestamp)) {
                return;
              }

              if (target[timestamp]) {
                target[timestamp] = {
                  ...target[timestamp],
                  y: target[timestamp].y + datapoint.value - firstValue,
                  hasIndicator:
                    target[timestamp].hasIndicator || !!datapoint.indicator,
                };
              } else {
                target[timestamp] = {
                  x: timestamp,
                  y: datapoint.value - firstValue,
                  hasIndicator: !!datapoint.indicator,
                };
              }
            });
          };

          refactorDatapoints(
            curr.chartData?.dataPoints?.noAlgorithmicProgression,
            acc.rawNapData,
            0,
          );

          refactorDatapoints(
            curr.chartData?.dataPoints.total.stable,
            acc.rawTotalStableData,
            0,
          );

          const firstNapValue =
            curr.chartData?.dataPoints?.noAlgorithmicProgression?.[0]?.value ??
            0;

          refactorDatapoints(
            curr.chartData?.dataPoints?.noAlgorithmicProgression,
            acc.nettoNapData,
            firstNapValue,
          );

          const firstStableValue =
            curr.chartData?.dataPoints?.total?.stable?.[0]?.value ?? 0;

          refactorDatapoints(
            curr.chartData?.dataPoints.total.stable,
            acc.nettoTotalStableData,
            firstStableValue,
          );

          // For the cost basis, get all custom transactions
          // For each custom transaction, find closest nap timestamp that is earlier if exists
          // Add this value to each nap timestamp

          // Or
          // Bundle all custom transactions over all subs
          // For each custom transaction, find closest nap timestamp that is earlier if exists
          // Add this value to each nap timestamp

          const customTransactions =
            curr.compactTransactions?.filter(isCustomTransaction) ?? [];

          const addToTimestamp = (
            timestamp: number,
            value: number,
            shouldAdd: boolean,
          ) => {
            // if (acc.rawCostBasisData[timestamp]) {
            //   acc.rawCostBasisData[timestamp] = {
            //     ...acc.rawCostBasisData[timestamp],
            //     y:
            //       acc.rawCostBasisData[timestamp].y +
            //       value * (shouldAdd ? 1 : -1),
            //   };
            // } else {
            //   acc.rawCostBasisData[timestamp] = {
            //     x: timestamp,
            //     y: value,
            //   };
            // }
          };

          customTransactions.forEach((customTransaction) => {
            // let firstIndexFound;

            // Find closest earlier timestamp in nap timestamps
            for (
              let index = 0;
              index < allPossibleNapTimestampsByTimeframe.length;
              index++
            ) {
              const timestamp = allPossibleNapTimestampsByTimeframe[index];

              if (timestamp < customTransaction.transactionTimestamp) {
                continue;
              }

              // if (!firstIndexFound) {
              //   firstIndexFound = true;

              //   if (index !== 0) {
              //     addToTimestamp(
              //       allPossibleNapTimestampsByTimeframe[index - 1],
              //       customTransaction.stableQuantity,
              //       customTransaction.transactionType.includes('ADD'),
              //     );
              //   }
              // }

              addToTimestamp(
                timestamp,
                customTransaction.stableQuantity,
                customTransaction.transactionType.includes('ADD'),
              );
            }
          });

          return acc;
        },
        {
          rawNapData: {},
          rawTotalStableData: {},
          nettoNapData: {},
          nettoTotalStableData: {},
          rawCostBasisData: {},
          //   rawCostBasisData: allPossibleNapTimestampsByTimeframe.reduce<
          //     Record<number, { x: number; y: number }>
          //   >((acc, curr) => {
          //     acc[curr] = { y: 0, x: curr };
          //     return acc;
          //   }, {}),
        },
      ),
    [allPossibleNapTimestampsByTimeframe, subscriptions],
  );

  const rawNapDataPoints = useMemo(
    () => Object.values(rawNapData).sort((a, b) => a.x - b.x),
    [rawNapData],
  );
  const nettoNapDataPoints = useMemo(
    () => Object.values(nettoNapData).sort((a, b) => a.x - b.x),
    [nettoNapData],
  );
  const napDataPoints = useMemo(
    () =>
      overviewChartType === OverviewChartType.ProfitOnly
        ? nettoNapDataPoints
        : rawNapDataPoints,
    [nettoNapDataPoints, overviewChartType, rawNapDataPoints],
  );
  const rawTotalStableDataPoints = useMemo(
    () => Object.values(rawTotalStableData).sort((a, b) => a.x - b.x),
    [rawTotalStableData],
  );
  const nettoTotalStableDataPoints = useMemo(
    () => Object.values(nettoTotalStableData).sort((a, b) => a.x - b.x),
    [nettoTotalStableData],
  );
  const totalStableDataPoints = useMemo(
    () =>
      overviewChartType === OverviewChartType.ProfitOnly
        ? nettoTotalStableDataPoints
        : rawTotalStableDataPoints,
    [nettoTotalStableDataPoints, overviewChartType, rawTotalStableDataPoints],
  );
  const rawCostBasisDataPoints = useMemo(
    () => Object.values(rawCostBasisData).sort((a, b) => a.x - b.x),
    [rawCostBasisData],
  );

  const rawTotalStableDataPointsMax = useMemo(
    () => Math.max(...totalStableDataPoints.map((datum) => datum.y)),
    [totalStableDataPoints],
  );
  const rawNapDataPointsMax = useMemo(
    () => Math.max(...napDataPoints.map((datum) => datum.y)),
    [napDataPoints],
  );
  const rawCostBasisDataPointsMax = useMemo(
    () => Math.max(...rawCostBasisDataPoints.map((datum) => datum.y)),
    [rawCostBasisDataPoints],
  );

  const stableValueMax = useMemo(() => {
    return Math.max(
      rawNapDataPointsMax,
      rawTotalStableDataPointsMax,
      rawCostBasisDataPointsMax,
    );
  }, [
    rawNapDataPointsMax,
    rawTotalStableDataPointsMax,
    rawCostBasisDataPointsMax,
  ]);

  const totalStableDataOriginalDomain = useMemo(() => {
    return totalStableDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / stableValueMax,
    }));
  }, [totalStableDataPoints, stableValueMax]);
  const napDataOriginalDomain = useMemo(() => {
    return napDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / stableValueMax,
    }));
  }, [napDataPoints, stableValueMax]);
  const costBasisDataOriginalDomain = useMemo(() => {
    return rawCostBasisDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / stableValueMax,
    }));
  }, [rawCostBasisDataPoints, stableValueMax]);

  const stableValueOriginalDomainMin = useMemo(() => {
    return Math.min(
      ...napDataOriginalDomain.map((d) => d.y),
      ...totalStableDataOriginalDomain.map((d) => d.y),
      ...costBasisDataOriginalDomain.map((d) => d.y),
    );
  }, [
    napDataOriginalDomain,
    totalStableDataOriginalDomain,
    costBasisDataOriginalDomain,
  ]);
  const stableValueOriginalDomainMax = useMemo(() => {
    return Math.max(
      ...napDataOriginalDomain.map((d) => d.y),
      ...totalStableDataOriginalDomain.map((d) => d.y),
      ...costBasisDataOriginalDomain.map((d) => d.y),
    );
  }, [
    napDataOriginalDomain,
    totalStableDataOriginalDomain,
    costBasisDataOriginalDomain,
  ]);

  const napData = useMemo(() => {
    return napDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        stableValueOriginalDomainMin,
        stableValueOriginalDomainMax,
      ),
    }));
  }, [
    napDataOriginalDomain,
    stableValueOriginalDomainMin,
    stableValueOriginalDomainMax,
  ]);
  const totalStableData = useMemo(() => {
    return totalStableDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        stableValueOriginalDomainMin,
        stableValueOriginalDomainMax,
      ),
    }));
  }, [
    totalStableDataOriginalDomain,
    stableValueOriginalDomainMin,
    stableValueOriginalDomainMax,
  ]);
  const costBasisData = useMemo(() => {
    return costBasisDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        stableValueOriginalDomainMin,
        stableValueOriginalDomainMax,
      ),
    }));
  }, [
    costBasisDataOriginalDomain,
    stableValueOriginalDomainMin,
    stableValueOriginalDomainMax,
  ]);

  const dataSets = useMemo(() => {
    if (!totalStableData?.length || !napData?.length) {
      return [[]];
    }

    const data = [];

    if (totalStableData?.length) {
      data.push(totalStableData);
    }
    if (napData?.length) {
      data.push(napData);
    }
    if (costBasisData?.length) {
      data.push(costBasisData);
    }

    return data;
  }, [napData, totalStableData, costBasisData]);

  const isPositiveChart = useMemo(() => {
    if (!totalStableDataPoints?.length) {
      return true;
    }

    return (
      totalStableDataPoints[totalStableDataPoints.length - 1].y >=
      totalStableDataPoints[0].y
    );
  }, [totalStableDataPoints]);

  const getChartColor = useCallback(
    (chartType: ChartType) => {
      switch (chartType) {
        case ChartType.TotalStableBalance:
          return {
            fill: `url(#${
              isPositiveChart ? 'positiveStableColor' : 'negativeStableColor'
            })`,
            stroke: isPositiveChart ? colors.mossGreen : colors.red,
          };

        case ChartType.NoAlgoritmicProgression:
          return {
            fill: 'url(#napColor)',
            stroke: colors.white,
          };
      }
    },
    [isPositiveChart],
  );

  const selectedChartTypes = useMemo(
    () => [ChartType.TotalStableBalance, ChartType.NoAlgoritmicProgression],
    [],
  );

  // const availableChartData = useMemo(
  //   () =>
  //     subscriptions.reduce((acc, curr) => acc + (curr.chartData ? 1 : 0), 0),
  //   [subscriptions],
  // );

  const { animatedDataPoints, domain } = useAnimatedChartData(
    useMemo(
      () =>
        `${overviewChartTimeframe}_${overviewChartType}_${subscriptions.length}`,
      [overviewChartTimeframe, overviewChartType, subscriptions.length],
    ),
    dataSets,
    useMemo(
      () => [ChartType.TotalStableBalance, ChartType.NoAlgoritmicProgression],
      [],
    ),
    false,
    2,
  );

  const hasData = useMemo(() => dataSets.some((d) => !!d.length), [dataSets]);

  const referenceLineTimestamp = moment('2026-04-12').valueOf();
  const referenceLineLabel = 'TPT';

  const startTPTReferenceLine = useMemo(() => {
    return [
      { x: referenceLineTimestamp, y: 0 },
      { x: referenceLineTimestamp, y: 1 },
    ];
  }, [referenceLineTimestamp]);

  const startTPTReferenceLabel = useMemo(() => {
    return { x: referenceLineTimestamp, y: 1, label: referenceLineLabel };
  }, [referenceLineTimestamp, referenceLineLabel]);

  // AXIS
  const calculateTickValue = useCallback(
    (t: Date) => {
      if (!domain?.x) {
        return moment(t).format('DD MMM');
      }

      const formatUnit = getFormatUnit(
        domain.x[0] as number,
        domain.x[1] as number,
      );

      return moment(t).format(formatUnit);
    },
    [domain?.x],
  );

  const axisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 14, padding: 5, fill: colors.white },
    }),
    [],
  );

  const tickValues = useMemo(() => {
    if (!domain?.x[0]) {
      return [];
    }

    return createXTickValues(domain.x[0] as number);
  }, [domain?.x]);

  const calculateYTickValue = useCallback(
    (t: any) => {
      const max = stableValueMax;
      const minO = stableValueOriginalDomainMin;
      const maxO = stableValueOriginalDomainMax;

      /** Reverse the stretch to get the original value */
      const y = maxO - (1 - t) * ((maxO - minO) * 1.05);
      const balance = isNaN(y * max) ? 0 : y * max;

      return `${formatPrice(
        balance,
        true,
        (maxO - minO) / maxO < 0.01 ? 2 : 0,
      )}`;
    },
    [
      stableValueMax,
      stableValueOriginalDomainMax,
      stableValueOriginalDomainMin,
    ],
  );

  const yTicks = useMemo(() => {
    const dataPoints = [...totalStableDataPoints, ...napDataPoints];
    if (!dataPoints?.length) return [];

    const yValues = dataPoints.map((p) => p.y);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);
    if (minY === maxY) return [Math.round(minY)];

    const range = maxY - minY;
    const roughStep = range / (8 - 1);

    // Determine step size based on magnitude, prioritizing "pretty" steps (e.g., 1000, 100, 10)
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const step = Math.max(1, Math.round(roughStep / magnitude) * magnitude);

    // Round minY down and maxY up to nearest step for clean ticks
    const start = Math.floor(minY / step) * step;
    const ticks: number[] = [];

    for (let tick = start; tick <= maxY + step; tick += step) {
      ticks.push(
        stretchYValueOnSharedDomain(
          Number(tick.toFixed(4)) / stableValueMax,
          stableValueOriginalDomainMin,
          stableValueOriginalDomainMax,
        ),
      );
    }

    return ticks;
  }, [
    napDataPoints,
    totalStableDataPoints,
    stableValueMax,
    stableValueOriginalDomainMax,
    stableValueOriginalDomainMin,
  ]);

  const yAxisInformation = useMemo(() => {
    const longestTickWidth = calculateLongestTickWidth(yTicks, (t: number) =>
      calculateYTickValue(t),
    );
    const yAxisWidth = calculateYAxisWidth(longestTickWidth);
    return { width: yAxisWidth, longestTickWidth };
  }, [calculateYTickValue, yTicks]);

  const yAxisStyle = useMemo(() => {
    const width = yAxisInformation.width || 0;
    const longestTickWidth = yAxisInformation.longestTickWidth ?? 0;

    return {
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: {
        fontSize: 16,
        padding: (width - longestTickWidth) / 3,
        fill: colors.white,
      },
    };
  }, [yAxisInformation]);

  const chartPadding = useMemo(
    () => ({
      left: 30,
      top: 25,
      right: yAxisInformation.width ?? 60,
      bottom: 20,
    }),
    [yAxisInformation],
  );

  // We need some chart information for the
  useEffect(() => {
    if (
      !rawTotalStableDataPoints?.length ||
      rawTotalStableDataPoints.length < 2 ||
      !rawNapDataPoints?.length ||
      !allPossibleNapTimestamps?.length
    ) {
      return;
    }

    const latestStableValue =
      rawTotalStableDataPoints[rawTotalStableDataPoints.length - 1].y;
    const yesterdayStableValue =
      rawTotalStableDataPoints[rawTotalStableDataPoints.length - 2].y;
    const dayChange = latestStableValue - yesterdayStableValue;
    const dayChangeRelative =
      (latestStableValue - yesterdayStableValue) / yesterdayStableValue;

    const latestNapValue = rawNapDataPoints[rawNapDataPoints.length - 1].y;
    const napDifference = latestStableValue - latestNapValue;
    const napDifferenceRelative =
      (latestStableValue - latestNapValue) / latestNapValue;

    const amountOfDays = Math.round(
      (allPossibleNapTimestamps[allPossibleNapTimestamps.length - 1] -
        allPossibleNapTimestamps[0]) /
        (1000 * 60 * 60 * 24),
    );

    const information = {
      dayChange,
      dayChangeRelative,
      napDifference,
      napDifferenceRelative,
      amountOfDays,
    };

    setSummaryInformation(information);
  }, [
    allPossibleNapTimestamps,
    rawNapDataPoints,
    rawTotalStableDataPoints,
    setSummaryInformation,
  ]);

  return (
    <div
      ref={rootRef}
      style={{
        position: 'relative',
        opacity: rootDimensions ? 1 : 0,
        transition: 'opacity 500ms ease-in-out',
        height: '100%',
        width: '100%',
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <svg style={{ position: 'absolute' }}>
        <defs>
          <linearGradient
            id="positiveStableColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.mossGreen} stopOpacity={0.3} />
            <stop
              offset="100%"
              stopColor={colors.mossGreen}
              stopOpacity={0.04}
            />
          </linearGradient>
          <linearGradient
            id="negativeStableColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.red} stopOpacity={0.4} />
            <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
          </linearGradient>
          {/* <linearGradient
            id={`botColor_${subscription.baseAsset}`}
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={botColor} stopOpacity={0.6} />
            <stop offset="100%" stopColor={botColor} stopOpacity={0.04} />
          </linearGradient> */}
          <linearGradient id="napColor" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={colors.white} stopOpacity={0.4} />
            <stop offset="100%" stopColor={colors.white} stopOpacity={0.04} />
          </linearGradient>
          <linearGradient
            id="positiveMonthGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.green} stopOpacity={0.3} />
            <stop offset="100%" stopColor={colors.green} stopOpacity={0.08} />
          </linearGradient>
          <linearGradient
            id="negativeMonthGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.red} stopOpacity={0.3} />
            <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
          </linearGradient>
          <pattern
            id="orangeStripes"
            patternUnits="userSpaceOnUse"
            width="4"
            height="4"
            patternTransform="rotate(45)"
          >
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="4"
              stroke={colors.orange}
              strokeWidth="2"
              opacity="0.7"
            />
          </pattern>
        </defs>
      </svg>

      {!!rootDimensions && !!hasData && (
        <>
          <VictoryChart
            height={rootDimensions.height}
            width={rootDimensions.width}
            scale="linear"
            padding={chartPadding}
            domain={domain}
            domainPadding={{ y: 10 }}
          >
            <VictoryAxis
              dependentAxis
              style={yAxisStyle}
              orientation={'right'}
              tickFormat={(t: number) => calculateYTickValue(t)}
              width={yAxisInformation.width}
              tickValues={yTicks}
            />
            <VictoryAxis
              tickFormat={calculateTickValue}
              style={axisStyle}
              tickValues={tickValues}
              tickCount={6}
              fixLabelOverlap
            />
            {animatedDataPoints.slice(0, 2).map((selectedDataPoint, index) => (
              <VictoryArea
                key={`chart_${index}`}
                data={selectedDataPoint}
                style={{
                  data: {
                    ...getChartColor(selectedChartTypes[index]),
                    strokeWidth: 1,
                  },
                }}
              />
            ))}
            <VictoryLine
              data={startTPTReferenceLine}
              style={{
                data: {
                  stroke: 'url(#orangeStripes)',
                  strokeWidth: 5,
                },
              }}
            />
            <VictoryScatter
              data={[startTPTReferenceLabel]}
              size={0}
              labels={({ datum }) => datum.label}
              labelComponent={
                <VictoryLabel
                  style={{
                    fill: colors.orange,
                    fontWeight: 'bold',
                    fontSize: 10,
                    opacity: 0.7,
                  }}
                  textAnchor="end"
                  dy={8}
                  dx={-8}
                />
              }
            />
            {/* {animatedDataPoints.slice(-1).map((selectedDataPoint, index) => (
              <VictoryLine
                key={`chart_${index}`}
                data={selectedDataPoint}
                style={{
                  data: {
                    stroke: colors.orange,
                    strokeWidth: 1,
                  },
                }}
              />
            ))} */}
          </VictoryChart>
        </>
      )}

      {!!rootDimensions && !hasData && (
        <LoadingIcons.Grid width={20} height={20} />
      )}
    </div>
  );
};

export default SummaryChart;
